import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { FormsModule, UntypedFormGroup } from '@angular/forms';

import { BehaviorSubject, Observable, startWith, takeUntil } from 'rxjs';

import {
  DateRangeType,
  DayOfWeekAbbrMapping,
  DayOfWeekToBitMapping,
  DaysOfWeek,
  DisplaySchema,
  IUserExperience,
  OnlyExpandDayOfWeek,
  OrderByWeekType,
  SummitDisposing,
  SummitIdObject,
  SummitUserExperienceService,
  ViewField
} from '@summit/models';
import { ApiError } from '@summit/ui';
import { Button } from 'primeng/button';
import { SelectButton } from 'primeng/selectbutton';

@Component({
  selector: 's-form-input-day-of-week',
  templateUrl: './s-form-input-day-of-week.component.html',
  styleUrls: ['./s-form-input-day-of-week.component.scss'],
  standalone: true,
  imports: [FormsModule, SelectButton, Button],
  changeDetection: ChangeDetectionStrategy.OnPush
})
//export class SFormInputDayOfWeekComponent<T extends SummitIdObject> extends SummitControl<T> implements OnInit {
export class SFormInputDayOfWeekComponent<T extends SummitIdObject> extends SummitDisposing implements OnInit {
  @Input() weekType: DateRangeType.TenantWeek | DateRangeType.ScheduleWeek | DateRangeType.SettlementWeek;
  @Input() label: string;
  @Input() form: UntypedFormGroup;
  @Input() fieldName: keyof T | string;
  @Input() groupName: string;
  @Input() apiError: ApiError;
  @Input() schema: DisplaySchema<T>;
  @Input() nullableLabel: string;
  @Input() displayHint: string;
  @Input() multiPick = true;
  @Input() NotForm = false;
  @Input() toggle = false;
  @Input() disableDates: Array<number> = [];
  @Input() groupButtons = true;
  @Input() defaultSelectedDay: Array<number> = [];
  @Input() resetButton = false;
  @Input() writeAsArray = false;
  @Output() changeEvent = new EventEmitter<number | undefined>();
  attribute?: ViewField<T>;
  selectedDays: number[] = [];
  selectedDay?: number;
  daysOfWeek: Array<{ id: string; name: string; code: number; disabled: boolean }> = [];
  arr: number[] = [];
  //added for notForm
  selectedDaysSubject = new BehaviorSubject<number[]>([]);
  emittedDays: Observable<number[]> = this.selectedDaysSubject.asObservable();
  weekendsDisabled = false;
  weekdaysDisabled = false;

  constructor(
    private uxService: SummitUserExperienceService<IUserExperience>,
    private cd: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.weekType ??= DateRangeType.TenantWeek;
    this.uxService.currentUserExperience.pipe(takeUntil(this.disposing$)).subscribe((ux) => {
      //get button order
      const dayOrderNo = OrderByWeekType(ux.activeEmployee, this.weekType);

      this.daysOfWeek = [
        {
          id: '0',
          name: DayOfWeekAbbrMapping[dayOrderNo[0]],
          code: dayOrderNo[0],
          disabled: this.disableDates.includes(dayOrderNo[0])
        },
        {
          id: '1',
          name: DayOfWeekAbbrMapping[dayOrderNo[1]],
          code: dayOrderNo[1],
          disabled: this.disableDates.includes(dayOrderNo[1])
        },
        {
          id: '2',
          name: DayOfWeekAbbrMapping[dayOrderNo[2]],
          code: dayOrderNo[2],
          disabled: this.disableDates.includes(dayOrderNo[2])
        },
        {
          id: '3',
          name: DayOfWeekAbbrMapping[dayOrderNo[3]],
          code: dayOrderNo[3],
          disabled: this.disableDates.includes(dayOrderNo[3])
        },
        {
          id: '4',
          name: DayOfWeekAbbrMapping[dayOrderNo[4]],
          code: dayOrderNo[4],
          disabled: this.disableDates.includes(dayOrderNo[4])
        },
        {
          id: '5',
          name: DayOfWeekAbbrMapping[dayOrderNo[5]],
          code: dayOrderNo[5],
          disabled: this.disableDates.includes(dayOrderNo[5])
        },
        {
          id: '6',
          name: DayOfWeekAbbrMapping[dayOrderNo[6]],
          code: dayOrderNo[6],
          disabled: this.disableDates.includes(dayOrderNo[6])
        }
      ];
      this.cd.detectChanges();
    });

    this.weekendsDisabled =
      this.disableDates.findIndex((disDay) => disDay == 0) != -1 &&
      this.disableDates.findIndex((disDay) => disDay == 6) != -1
        ? true
        : false;

    this.weekdaysDisabled =
      this.disableDates.findIndex((disDay) => disDay == 1) != -1 &&
      this.disableDates.findIndex((disDay) => disDay == 2) != -1 &&
      this.disableDates.findIndex((disDay) => disDay == 3) != -1 &&
      this.disableDates.findIndex((disDay) => disDay == 4) != -1 &&
      this.disableDates.findIndex((disDay) => disDay == 5) != -1
        ? true
        : false;

    //if it IS on a form
    if (!this.NotForm) {
      this.attribute = this.schema?.getAttribute(this.fieldName as keyof T & string, this.groupName);
      if (!this.attribute) {
        console.error(
          `Could not find Property: ${this.fieldName?.toString()} in group ${this.groupName} in Tree:`,
          this.schema
        );
      }

      this.label = this.label ?? this.attribute?.displayName;

      //get previously selected days
      if (this.form && this.fieldName) {
        this.form.controls[this.fieldName.toString()].valueChanges
          .pipe(startWith(this.form.controls[this.fieldName.toString()].value), takeUntil(this.disposing$))
          .subscribe((value) => {
            if (Array.isArray(value)) {
              this.selectedDays = value.length > 0 ? value : [];
              this.selectedDay = value.length > 0 ? this.selectedDays[0] : undefined;
              this.cd.detectChanges();
            } else {
              //console.log(`${this.fieldName.toString()}`, value);
              const valueInt = parseInt(value, 10);
              this.selectedDays = OnlyExpandDayOfWeek(valueInt);
              this.selectedDay = this.selectedDays.length > 0 ? this.selectedDays[0] : undefined;
              this.cd.detectChanges();
            }
          });
      }
    }

    //if single day selection, get previously selected day
    if (!this.multiPick) {
      this.selectedDay = this.selectedDays[0];
    }

    if (this.defaultSelectedDay.length > 0) {
      this.selectedDays = this.defaultSelectedDay;
    }
  }

  selectWDGroupE($event) {
    $event.stopPropagation();
    $event.preventDefault();
    const weekdays = [1, 2, 3, 4, 5];
    this.selectedDays = [];
    weekdays.forEach((weekday) => {
      if (this.disableDates.findIndex((disDay) => disDay == weekday) == -1) this.selectedDays.push(weekday);
    });
    this.onChange();
  }
  selectWEGroupE($event) {
    $event.stopPropagation();
    $event.preventDefault();
    const weekends = [0, 6];
    this.selectedDays = [];
    weekends.forEach((weekend) => {
      if (this.disableDates.findIndex((disDay) => disDay == weekend) == -1) this.selectedDays.push(weekend);
    });
    this.onChange();
  }
  selectAllGroupE($event) {
    $event.stopPropagation();
    $event.preventDefault();
    const allDays = [0, 1, 2, 3, 4, 5, 6];
    this.selectedDays = [];
    allDays.forEach((day) => {
      if (this.disableDates.findIndex((disDay) => disDay == day) == -1) this.selectedDays.push(day);
    });
    this.onChange();
  }
  clearGroupE($event) {
    $event.stopPropagation();
    $event.preventDefault();
    this.selectedDays = [];
    this.onChange();
  }
  onChange($event?) {
    //turns the selected day grey
    if (this.multiPick) {
      this.selectedDays = $event?.value ?? this.selectedDays;
    } else {
      this.selectedDay = $event?.value;
      //console.log('selectedDay', this.selectedDay);
      if (this.toggle && this.selectedDay === undefined) return; //clear
      //standardized to array for rest of function
      this.selectedDays = this.selectedDay || this.selectedDay === 0 ? [this.selectedDay] : [];
    }
    if (this.NotForm) {
      this.selectedDaysSubject.next([...this.selectedDays]);
    }

    if (Number.isInteger(this.selectedDays)) {
      this.arr = [this.selectedDays as unknown as number];
    } else {
      this.arr = this.selectedDays;
    }
    const bitDayNo = this.arr.map((dayNo) => DayOfWeekToBitMapping[dayNo]);
    const newValue = bitDayNo.length == 0 ? undefined : this.ReduceDayOfWeek(bitDayNo);
    if (this.NotForm === false) {
      if (this.writeAsArray) {
        const newFormValue = OnlyExpandDayOfWeek(newValue ?? DaysOfWeek.None);
        //console.log('newValue', newFormValue, this.form?.controls[this.fieldName.toString()].value);
        this.form?.controls[this.fieldName.toString()].setValue(newFormValue);
      } else {
        const newFormValue = newValue ?? DaysOfWeek.None;
        //console.log('newValue', newFormValue, this.form?.controls[this.fieldName.toString()].value);
        this.form?.controls[this.fieldName.toString()].setValue(newFormValue);
      }
    }
    //console.log('newValue', newValue);
    this.changeEvent.emit(newValue);
    if (this.toggle) {
      //console.log('setting to empty');
      this.selectedDays = [];
      this.selectedDay = undefined;
    }
    this.cd.detectChanges();
    //console.log('final', this.selectedDays, this.selectedDay);
  }

  ReduceDayOfWeek(input: DaysOfWeek[]) {
    const selectedDaysNum: number = input.reduce(function (a, b) {
      return a | b;
    }, 0);
    return selectedDaysNum;
  }
  resetGroup() {
    if (this.defaultSelectedDay.length > 0) this.selectedDays = this.defaultSelectedDay;
    else this.selectedDays = [];
  }

  public reset() {
    this.selectedDay = undefined;
    this.selectedDays = [];
    this.onChange();
  }
}
